import { useCallback, useRef, useState, useEffect, useMemo, type ReactNode } from 'react'
import { useAuthStore } from '../stores/authStore'
import { logger } from '../utils/logger'
import {
  WebSocketContext,
  type MessageListener,
  type ReconnectCallback,
} from './WebSocketContextBase'

const WS_URL = import.meta.env.VITE_WEBSOCKET_URL || 'ws://localhost:4510'
const RECONNECT_DELAY = 3000 // 3秒後に再接続
const MAX_RECONNECT_ATTEMPTS = 10
const HEARTBEAT_INTERVAL = 30000 // 30秒ごとにpingを送信

/**
 * WebSocketコンテキストプロバイダー
 * アプリケーション全体で1つのWebSocket接続を共有
 */
export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const listenersRef = useRef(new Set<MessageListener>())
  const reconnectCallbacksRef = useRef(new Set<ReconnectCallback>())
  const { user, getIdToken } = useAuthStore()
  const lastTokenRef = useRef<string | null>(null)

  // WebSocket接続を確立（IDトークンを使用 — custom:organizationIdを含むため）
  const connect = useCallback(() => {
    const token = getIdToken()

    if (!token) {
      logger.warn('[WebSocketContext] トークンがありません。接続をスキップします')
      return
    }

    // 既存接続が有効かつ同じトークンなら何もしない
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && lastTokenRef.current === token) {
      return
    }

    // 既存の接続をクリーンアップ（正常コードを付けて閉じる）
    if (wsRef.current) {
      wsRef.current.close(1000, 'reconnect')
      wsRef.current = null
    }

    try {
      const wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}`
      logger.debug('[WebSocketContext] 接続試行:', { url: WS_URL, hasToken: !!token })
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws
      lastTokenRef.current = token

      ws.onopen = () => {
        logger.info('[WebSocketContext] 接続成功')
        setConnected(true)
        const wasReconnecting = reconnectAttemptsRef.current > 0
        reconnectAttemptsRef.current = 0

        // ハートビート開始
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }))
          }
        }, HEARTBEAT_INTERVAL)

        // 再接続成功時のコールバック（サーバー同期用）
        if (wasReconnecting) {
          logger.info('[WebSocketContext] 再接続成功 - サーバー同期を実行')
          reconnectCallbacksRef.current.forEach(callback => {
            try {
              callback()
            } catch (error) {
              logger.error('[WebSocketContext] 再接続コールバックエラー:', error)
            }
          })
        }
      }

      ws.onmessage = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data)
          // pongメッセージは無視
          if (message.action === 'pong') {
            return
          }

          logger.debug('[WebSocketContext] メッセージ受信:', message)

          // グループメンバーシップ変更イベントを処理
          if (message.eventType === 'group.membership.changed' && message.data?.requireReconnect) {
            logger.info('[WebSocketContext] グループメンバーシップ変更を検出 - WebSocket再接続を実行')
            // 再接続カウンターをリセットして即座に再接続
            reconnectAttemptsRef.current = 0
            // 現在の接続を閉じて再接続（最新のグループ情報を取得）
            if (wsRef.current) {
              wsRef.current.close(1000, 'group-membership-changed')
            }
            return
          }

          // 登録されたすべてのリスナーに通知
          listenersRef.current.forEach(listener => {
            try {
              listener(message)
            } catch (error) {
              logger.error('[WebSocketContext] リスナーエラー:', error)
            }
          })
        } catch (error) {
          logger.error('[WebSocketContext] メッセージパースエラー:', error)
        }
      }

      ws.onerror = (error) => {
        logger.error('[WebSocketContext] エラー発生:', error)
      }

      ws.onclose = (event: CloseEvent) => {
        logger.debug('[WebSocketContext] 接続クローズ:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          reconnectAttempts: reconnectAttemptsRef.current
        })
        setConnected(false)

        // ハートビート停止
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
          heartbeatIntervalRef.current = null
        }

        // 自動再接続（最大試行回数まで）
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1
          logger.info(`[WebSocketContext] ${RECONNECT_DELAY/1000}秒後に再接続試行 (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`)
          reconnectTimeoutRef.current = setTimeout(() => {
            connect()
          }, RECONNECT_DELAY)
        } else {
          logger.error('[WebSocketContext] 最大再接続回数に達しました。再接続を停止します')
        }
      }
    } catch (error) {
      logger.error('[WebSocketContext] 接続エラー:', error)
    }
  }, [getIdToken])

  // メッセージ送信
  const sendMessage = useCallback((message: unknown): boolean => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
      return true
    } else {
      logger.warn('[WebSocketContext] WebSocketが接続されていません。メッセージを送信できません')
      return false
    }
  }, [])

  // 手動再接続
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0
    connect()
  }, [connect])

  // メッセージリスナーを追加
  const addListener = useCallback((listener: MessageListener) => {
    listenersRef.current.add(listener)
    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  // 再接続コールバックを追加
  const addReconnectCallback = useCallback((callback: ReconnectCallback) => {
    reconnectCallbacksRef.current.add(callback)
    return () => {
      reconnectCallbacksRef.current.delete(callback)
    }
  }, [])

  // スリープ復帰・タブ復帰・ネットワーク復帰時の再接続:
  // スリープ中に接続が死んだ場合、onclose の自動再接続は MAX_RECONNECT_ATTEMPTS を
  // 使い切って永久に停止している可能性がある。またトークンもスリープ中に失効しうる。
  // そのためページが可視/オンラインに戻ったら、トークンを更新したうえで明示的に再接続する。
  useEffect(() => {
    const handleWakeup = () => {
      if (document.hidden) return
      const { user: currentUser, refreshToken } = useAuthStore.getState()
      if (!currentUser) return

      const ws = wsRef.current
      if (ws && ws.readyState === WebSocket.CONNECTING) return
      if (ws && ws.readyState === WebSocket.OPEN) {
        // スリープ復帰直後は「readyState は OPEN だが実際は死んでいる」接続がありうる。
        // ping を送ると TCP 断が早期検出され、死んでいれば onclose → 自動再接続に乗る。
        try {
          ws.send(JSON.stringify({ action: 'ping' }))
        } catch {
          // 送信失敗時は onclose 経由の再接続に任せる
        }
        return
      }

      logger.info('[WebSocketContext] ページ復帰を検出 - トークン更新後に再接続')
      refreshToken()
        .catch((error) => {
          // リフレッシュ失敗 (ネットワーク未復帰等) でも既存トークンで再接続を試みる。
          // トークンが本当に失効していれば接続が失敗し、次の復帰イベントで再試行される
          logger.warn('[WebSocketContext] トークンリフレッシュ失敗 - 既存トークンで再接続:', error)
        })
        .finally(() => {
          reconnectAttemptsRef.current = 0
          connect()
        })
    }

    document.addEventListener('visibilitychange', handleWakeup)
    window.addEventListener('focus', handleWakeup)
    window.addEventListener('online', handleWakeup)
    return () => {
      document.removeEventListener('visibilitychange', handleWakeup)
      window.removeEventListener('focus', handleWakeup)
      window.removeEventListener('online', handleWakeup)
    }
  }, [connect])

  // コンポーネントマウント時に接続
  useEffect(() => {
    const token = getIdToken()
    if (user && token) {
      logger.debug('[WebSocketContext] ユーザーとトークンが利用可能 - 接続を開始')
      connect()
    } else if (user && !token) {
      logger.warn('[WebSocketContext] ユーザーは存在するがトークンがありません - 接続を保留')
    }

    // クリーンアップ
    return () => {
      logger.debug('[WebSocketContext] クリーンアップ実行')
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
        heartbeatIntervalRef.current = null
      }
      if (wsRef.current) {
        logger.debug('[WebSocketContext] WebSocket接続をクローズ')
        // イベントハンドラーを無効化して再接続を防ぐ
        wsRef.current.onclose = null
        wsRef.current.onerror = null
        wsRef.current.close(1000, 'cleanup')
        wsRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, connect])

  const value = useMemo(() => ({
    connected,
    sendMessage,
    reconnect,
    addListener,
    addReconnectCallback,
  }), [connected, sendMessage, reconnect, addListener, addReconnectCallback])

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}
